import { ShareableEntity } from "@/lib/api/shareable-entities";
import { HubEmptyState } from "./hub-empty-state";
import { NoteCard } from "./notes/note-card";
import { MomentCard } from "./moments/moment-card";
import { SwapCard } from "./swaps";
import { ConnectMomentHubCard } from "./connect-moment/connect-moment-hub-card";

interface EntityListProps {
  entities: ShareableEntity[];
  onEntityClick: (
    entityId: string,
    entityType: string,
    activityType?: string,
  ) => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  // isNew is computed against the current user's share record; admins have no
  // share record so the BE returns isNew=true for every mentee-created entity.
  // Set to false to suppress the badge for non-mentor viewers.
  showNewBadges?: boolean;
}

function renderEntityCard(
  entity: ShareableEntity,
  onEntityClick: (
    entityId: string,
    entityType: string,
    activityType?: string,
  ) => void,
  showNewBadges: boolean,
) {
  const isNew = showNewBadges ? (entity.isNew ?? false) : false;
  switch (entity.entityType) {
    case "note":
      if (!entity.note) {
        return null;
      }
      return (
        <NoteCard
          entity={
            entity as ShareableEntity & {
              note: NonNullable<ShareableEntity["note"]>;
            }
          }
          isNew={isNew}
          onClick={() => onEntityClick(entity.id, entity.entityType)}
        />
      );
    case "moment":
      if (!entity.moment) {
        return null;
      }
      return (
        <MomentCard
          entity={
            entity as ShareableEntity & {
              moment: NonNullable<ShareableEntity["moment"]>;
            }
          }
          isNew={isNew}
          onClick={() => onEntityClick(entity.id, entity.entityType)}
        />
      );
    case "swap":
      if (!entity.swap) {
        return null;
      }
      return (
        <SwapCard
          entity={
            entity as ShareableEntity & {
              swap: NonNullable<ShareableEntity["swap"]>;
            }
          }
          isNew={isNew}
          onClick={() => onEntityClick(entity.id, entity.entityType)}
        />
      );
    case "activity": {
      const activity = entity.activity;
      if (!activity || activity.activityType !== "connect_moment") {
        return null;
      }
      return (
        <ConnectMomentHubCard
          entity={entity}
          isNew={isNew}
          onClick={() =>
            onEntityClick(entity.id, entity.entityType, activity.activityType)
          }
        />
      );
    }
    default:
      return null;
  }
}

export function EntityList({
  entities,
  onEntityClick,
  emptyStateTitle,
  emptyStateDescription,
  showNewBadges = true,
}: EntityListProps) {
  if (entities.length === 0) {
    return (
      <HubEmptyState
        title={emptyStateTitle || "No activities yet"}
        description={
          emptyStateDescription ||
          "Activities will appear here once they're added."
        }
      />
    );
  }

  return (
    <div className="space-y-2">
      {entities.map((entity) => {
        const card = renderEntityCard(entity, onEntityClick, showNewBadges);
        if (!card) return null;
        return <div key={entity.id}>{card}</div>;
      })}
    </div>
  );
}
